import React, { useEffect, useState } from "react";
import { Backdrop, Box, Drawer, Stack, useMediaQuery } from "@mui/material";
import { SIDE_DRAWER_WIDTH } from "../../enums/themeEnum";
import { useTheme } from "@emotion/react";
import { SideBarContent } from "../ui/sidebar/SidebarContent";
import { GenericIconButton } from "../generic/GenericIconButton";
import { X } from "@phosphor-icons/react";
import { Topbar } from "../ui/topbar/Topbar";
import { router, usePage } from "@inertiajs/react";
import useStore from "../../states/store";
import { getNotificationsCount, getUserDetails, getUserPreference } from "../../config/apisauce";
import { useLogoutUser } from "../generic/functions/genericFunctions";
import { SearchField } from "../generic/GenericTextFields";
import { GUEST_ROUTES } from "../../routes/PageList";
import { COLLAB_KEY } from "../../enums/generalEnums";
import { AUTO_LOGOUT_STATUS } from "../../enums/statusEnums";
import NotificationDialog from "../Dialog/NotificationDialog";

const MainLayout = ({ children }) => {
  const [
    { loggedIndUserDetails },
    {
      permissions,
      setLoggedIndUserDetails,
      setPermissions,
      setUserPreference,
      setNotificationsCount,
      setPopularTag,
      setSearchValue,
      setCreatorFilters,
      setReferrer,
    },
  ] = useStore();
  const handleLogout = useLogoutUser();
  const { auth } = usePage().props;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { url } = usePage();
  const [path, query] = url.split('?');
  const params = new URLSearchParams(query);
  const hashValue = params.get('hash');
  const resetPasswordHash = params.get('passwordHash');
  const changeEmailHash = params.get('changeEmailHash');
  const collabHash = params.get('collabHash');
  const collabStatus = params.get('status');
  const referrer = params.get('ref');
  const pathName = new URL(url, window.location.origin).pathname;
  const [search, setSearch] = useState('');

  const handleToggleDrawer = () => {
    setIsDrawerOpen((prev) => !prev);
  };

  const handleRedirectToHome = () => {
    router.visit('/');
    setPopularTag(COLLAB_KEY);
  };

  const fetchLoggedInUserDetails = async () => {
    const response = await getUserDetails({ user_id: auth?.user?.id });
    if (response.ok) {
      setLoggedIndUserDetails({
        user_id: response?.data?.user_id,
        name: auth?.user?.name,
        email: auth?.user?.email,
        profession: response?.data?.profession,
        gender: response?.data?.gender,
        profile_picture_location: response?.data?.profile_picture?.location,
        country: response?.data?.country,
        role: auth?.user?.role,
        status: response?.data?.user?.status,
        instructions: response?.data?.instructions,
        contact_details: response?.data?.contact_details
      });
    }
  };

  const fetcthNotificationsCount = async () => {
    const response = await getNotificationsCount();
    if (response.ok) {
      setNotificationsCount(response?.data);
    }
  };

  const fetchUserPreference = async () => {
    const { ok, data } = await getUserPreference({ user_id: auth?.user?.id });
    if (ok) {
      setUserPreference(data?.preferences);
    }
  };

  const handleSearch = (isClear = false) => {
    router.visit('/collab-creators');
    setSearchValue(isClear ? '' : search);
    setPopularTag(COLLAB_KEY);
    setCreatorFilters({});
    if (isClear) setSearch('');
  };

  useEffect(() => {
    if (url === '/request-edit') {
      if (!auth?.user) {
        sessionStorage.setItem('loginDialogStatus', 'open');
        router.visit('/');
      } else {
        router.visit('/edit-profile');
      }
    };
  }, []);

  useEffect(() => {
    if (hashValue) {
      sessionStorage.setItem('hashValue', hashValue);
    };
    if (resetPasswordHash) {
      sessionStorage.setItem('resetPasswordHash', resetPasswordHash);
    };
    if (changeEmailHash) {
      sessionStorage.setItem('changeEmailHash', changeEmailHash);
    };
    if (collabHash && collabStatus) {
      if (auth?.user) {
        sessionStorage.setItem('collabHash', collabHash);
        sessionStorage.setItem('collabStatus', collabStatus);
        router.visit('/overview');
      } else {
        sessionStorage.setItem('loginDialogStatus', 'open');
        router.visit('/');
      }
    };
    if (referrer) {
      sessionStorage.setItem('sessionReferrer', referrer);
      setReferrer(referrer);
      window.addEventListener("load", function () {
        const url = new URL(window.location.href);
        url.searchParams.delete("ref");
        window.history.replaceState(null, "", url.toString());
      });
    };
    const handleBeforeUnload = () => {
      sessionStorage.removeItem('resetPasswordHash');
      sessionStorage.removeItem('collabHash', collabHash);
      sessionStorage.removeItem('collabStatus', collabStatus);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hashValue, resetPasswordHash, changeEmailHash, collabHash, collabStatus, referrer]);

  useEffect(() => {
    if (auth?.user) {
      fetchLoggedInUserDetails();
      fetchUserPreference();
      fetcthNotificationsCount();
      setPermissions(auth?.user?.permissions);
    }
    if (AUTO_LOGOUT_STATUS?.includes(auth?.user?.status)) {
      handleLogout();
    }
  }, [auth]);

  return (
    <Box sx={{ display: "flex", width: "100%" }}>
      <Backdrop
        open={isDrawerOpen}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer - 1,
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(0, 0, 0, 0.2)'
        }}
      />
      <Box component="nav" sx={{ width: { md: SIDE_DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isSmallScreen ? 'temporary' : 'persistent'}
          anchor="left"
          open={!isSmallScreen || isDrawerOpen}
          onClose={() => handleToggleDrawer()}
          sx={{
            '& .MuiDrawer-paper': {
              width: SIDE_DRAWER_WIDTH,
              boxSizing: 'border-box',
              border: 'none',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} mb={2} gap={2}>
              <Box sx={{ cursor: 'pointer', padding: '14px 0 0px 0px' }} onClick={() => handleRedirectToHome()}>
                <img src="/logos/wannacollab-logo-black.svg" alt="WannaCollab" style={{ width: '210px', height: '65px' }} />
              </Box>
              <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
                <GenericIconButton icon={<X size={24} color={'#02AFF0'} />} onClick={() => handleToggleDrawer()} />
              </Box>
            </Stack>
          </Box>
          <SideBarContent userDetails={auth} />
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{ display: "flex", flexDirection: "column", flexGrow: 1, overflowY: "auto", minHeight: "100vh" }}
      >
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: { xs: 0, md: SIDE_DRAWER_WIDTH },
            right: 0,
            zIndex: (theme) => theme.zIndex.appBar,
            backgroundColor: "white",
            height: "70px",
          }}
        >
          <Topbar handleToggleDrawer={handleToggleDrawer} userDetails={loggedIndUserDetails} />
        </Box>
        <Box sx={{ p: 2, flexGrow: 1, flexDirection: "column", display: "flex", mt: "70px" }}>
          {GUEST_ROUTES.includes(pathName) && (
            <Box sx={{ display: { xs: "flex", md: "none" }, mb: 2 }}>
              <SearchField
                width={'100%'}
                value={search}
                placeholder="Search for creators"
                disableSearchButton={false}
                handleChange={(value) => setSearch(value)}
                searchButtonAction={() => handleSearch()}
                handleClear={() => handleSearch(true)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
            </Box>
          )}
          {children}
        </Box>
      </Box>

      <NotificationDialog/>
    </Box>
  );
};

export default MainLayout;
